import React from 'react';
import { FormControl, Select, MenuItem } from '@mui/material';
import Loading from '../../../../components/loading/Loading';
import CustomButton from '../../../../components/buttons/CustomButton';
import OpportunityCard from './OpportunityCard';
import { formatDayLabel, plural } from '../utils';
import styles from '../Recovery.module.css';

const SELECT_SX = { fontSize: '0.82rem', minWidth: 150, background: '#FFFFFF' };

function Filters({ filters, onChange, professionals, procedures }) {
  const hasProfessional = professionals.some((p) => p.id === filters.professionalId);
  const hasProcedure = procedures.some((p) => p.id === filters.procedureId);

  return (
    <div className={styles.filters}>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Profissional</span>
        <FormControl size="small">
          <Select
            value={hasProfessional ? filters.professionalId : 0}
            onChange={(e) => onChange({ ...filters, professionalId: Number(e.target.value) })}
            sx={SELECT_SX}
          >
            <MenuItem value={0}>Todos</MenuItem>
            {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
        </FormControl>
      </div>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Procedimento</span>
        <FormControl size="small">
          <Select
            value={hasProcedure ? filters.procedureId : 0}
            onChange={(e) => onChange({ ...filters, procedureId: Number(e.target.value) })}
            sx={SELECT_SX}
          >
            <MenuItem value={0}>Todos</MenuItem>
            {procedures.map((p) => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>
      </div>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Dias</span>
        <FormControl size="small">
          <Select
            value={filters.days}
            onChange={(e) => onChange({ ...filters, days: Number(e.target.value) })}
            sx={{ ...SELECT_SX, minWidth: 110 }}
          >
            <MenuItem value={7}>Próximos 7</MenuItem>
            <MenuItem value={14}>Próximos 14</MenuItem>
          </Select>
        </FormControl>
      </div>
    </div>
  );
}

/**
 * Card "Horários livres": filtros, dias agrupados e um card por intervalo.
 * Os filtros ficam visíveis em qualquer estado para a pessoa poder mudar o
 * recorte mesmo sem resultado.
 */
function OpportunityList({ opportunities, filters, onFilterChange, onSelect, onRetry }) {
  const { status, days, professionals, procedures, error } = opportunities;

  function renderBody() {
    if (status === 'loading') return <div className={styles.loadingRow}><Loading /></div>;
    if (status === 'error') {
      return (
        <div className={styles.error}>
          <span>{error}</span>
          <CustomButton variant="outlined" style="primary" label="Tentar de novo" onClick={onRetry} />
        </div>
      );
    }
    if (days.length === 0) {
      return (
        <div className={styles.empty}>
          Nenhum horário livre nos próximos {filters.days} dias com esses filtros.
        </div>
      );
    }
    return days.map((day) => (
      <div key={day.date} className={styles.day}>
        <h3 className={styles.dayTitle}>
          {formatDayLabel(day.date, day.dayName)}
          <span className={styles.dayCount}>{plural(day.intervals.length, 'horário', 'horários')}</span>
        </h3>
        <div className={styles.dayGrid}>
          {day.intervals.map((interval) => (
            <OpportunityCard key={interval.id} interval={interval} onSelect={onSelect} />
          ))}
        </div>
      </div>
    ));
  }

  return (
    <section className={styles.card} aria-label="Horários livres">
      <header className={styles.cardHeader}>
        <div>
          <h2 className={styles.cardTitle}>Horários livres</h2>
          <p className={styles.cardSubtitle}>Clique num horário para ver quem chamar</p>
        </div>
        <Filters filters={filters} onChange={onFilterChange} professionals={professionals} procedures={procedures} />
      </header>
      <div className={styles.cardBody}>{renderBody()}</div>
    </section>
  );
}

export default OpportunityList;
